<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getThreadNavigationConversationId } from 'dashboard/helper/threadConversationRoute';
import { useConversationRoutePath } from 'dashboard/composables/useConversationRoutePath';
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import ConversationContextMenu from 'dashboard/components/widgets/conversation/contextMenu/Index.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const store = useStore();
const router = useRouter();
const { buildConversationPath } = useConversationRoutePath();

const currentChat = useMapGetter('getSelectedChat');
const uiFlags = useMapGetter('contactConversations/getUIFlags');

const contactGetter = useMapGetter('contacts/getContact');
const inboxGetter = useMapGetter('inboxes/getInbox');

const activeInbox = useMapGetter('getSelectedInbox');
const inboxesList = useMapGetter('inboxes/getInboxes');
const showInboxName = computed(
  () => !activeInbox.value && inboxesList.value.length > 1
);

const activeConversationId = computed(() =>
  getThreadNavigationConversationId(currentChat.value)
);

const currentInboxId = computed(() => currentChat.value?.inbox_id);
const currentContactInboxId = computed(
  () =>
    currentChat.value?.contact_inbox_id ||
    currentChat.value?.meta?.contact_inbox_id
);

const getConversationActivityAt = conversation =>
  conversation.last_activity_at ||
  conversation.timestamp ||
  conversation.created_at ||
  0;

const getConversationInboxGroupKey = conversation => {
  const contactInboxId =
    conversation.contact_inbox_id || conversation.meta?.contact_inbox_id;
  if (contactInboxId) return `contact_inbox:${contactInboxId}`;

  return `inbox:${conversation.inbox_id}`;
};

const isCurrentInboxThread = conversation => {
  const contactInboxId =
    conversation.contact_inbox_id || conversation.meta?.contact_inbox_id;

  if (contactInboxId && currentContactInboxId.value) {
    return contactInboxId === currentContactInboxId.value;
  }

  return conversation.inbox_id === currentInboxId.value;
};

const isOperationalThreadRow = conversation =>
  Number(conversation.id) ===
  Number(getThreadNavigationConversationId(conversation));

const dedupeLatestConversationPerInbox = conversationList => {
  const latestByInbox = {};

  conversationList.forEach(conversation => {
    const key = getConversationInboxGroupKey(conversation);
    const existing = latestByInbox[key];

    if (!existing) {
      latestByInbox[key] = conversation;
      return;
    }

    if (
      isOperationalThreadRow(conversation) &&
      !isOperationalThreadRow(existing)
    ) {
      latestByInbox[key] = conversation;
      return;
    }

    if (
      !isOperationalThreadRow(conversation) &&
      isOperationalThreadRow(existing)
    ) {
      return;
    }

    if (
      getConversationActivityAt(conversation) >=
      getConversationActivityAt(existing)
    ) {
      latestByInbox[key] = conversation;
    }
  });

  return Object.values(latestByInbox);
};

const contactConversationGetter = useMapGetter(
  'contactConversations/getContactConversation'
);
const conversations = computed(() =>
  contactConversationGetter.value(props.contactId)
);

const previousConversations = computed(() => {
  const filtered = conversations.value.filter(
    conversation => !isCurrentInboxThread(conversation)
  );

  return dedupeLatestConversationPerInbox(filtered).sort(
    (a, b) => getConversationActivityAt(b) - getConversationActivityAt(a)
  );
});

const buildConversationUrl = conversation =>
  buildConversationPath(getThreadNavigationConversationId(conversation));

const isConversationActive = conversation => {
  const conversationContactInboxId =
    conversation.contact_inbox_id || conversation.meta?.contact_inbox_id;

  if (conversationContactInboxId && currentContactInboxId.value) {
    return conversationContactInboxId === currentContactInboxId.value;
  }

  return (
    getThreadNavigationConversationId(conversation) ===
      activeConversationId.value || currentChat.value?.id === conversation.id
  );
};

const activeContextChat = ref(null);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

const conversationPath = computed(() => {
  if (!activeContextChat.value) return '';
  return buildConversationUrl(activeContextChat.value);
});

const onCardClick = (conversation, e) => {
  const path = buildConversationUrl(conversation);
  if (!path) return;

  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  router.push({ path });
};

const openContextMenu = (conversation, e) => {
  e.preventDefault();
  activeContextChat.value = conversation;
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
  activeContextChat.value = null;
};

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      showContextMenu.value = false;
      activeContextChat.value = null;
      store.dispatch('contactConversations/get', newId);
    }
  }
);

onMounted(() => {
  store.dispatch('contactConversations/get', props.contactId);
});
</script>

<template>
  <div v-if="!uiFlags.isFetching" class="">
    <div v-if="!previousConversations.length" class="no-label-message px-4 p-3">
      <span>
        {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
      </span>
    </div>
    <div
      v-else
      class="contact-conversation--list [&>.conversation:last-child]:!border-b-0 [&>.conversation:last-child:hover]:!border-b-0 [&>.conversation:last-child]:!rounded-b-lg"
    >
      <ConversationCard
        v-for="conversation in previousConversations"
        :key="getConversationInboxGroupKey(conversation)"
        :chat="conversation"
        :current-contact="contactGetter(conversation.meta?.sender?.id) || {}"
        :assignee="conversation.meta?.assignee || {}"
        :inbox="inboxGetter(conversation.inbox_id) || {}"
        :is-active-chat="isConversationActive(conversation)"
        :show-inbox-name="showInboxName"
        hide-thumbnail
        compact
        @click="onCardClick(conversation, $event)"
        @contextmenu="openContextMenu(conversation, $event)"
      />
    </div>
    <ContextMenu
      v-if="showContextMenu && activeContextChat"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="activeContextChat.status"
        :inbox-id="activeContextChat.inbox_id"
        :priority="activeContextChat.priority"
        :chat-id="activeContextChat.id"
        :has-unread-messages="activeContextChat.unread_count > 0"
        :conversation-labels="activeContextChat.labels"
        :conversation-url="conversationPath"
        :allowed-options="['open-new-tab', 'copy-link']"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
  <div v-else class="flex items-center justify-center py-5">
    <Spinner />
  </div>
</template>

<style lang="scss" scoped>
.no-label-message {
  @apply text-n-slate-11 mb-4;
}
</style>
